import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button, Slider, Select, Switch, Typography, Spin } from 'antd';

const { Text } = Typography;

/**
 * GrainEditorPanel — Film grain / noise texture effect editor.
 * Adds photographic grain with controls for intensity, size, color,
 * blend mode, shadows-only, temperature shift, and vignette.
 */
const GrainEditorPanel = ({ visible, onClose, onApply, sourceImageUrl, proxyUrl }) => {
    const previewCanvasRef = useRef(null);
    const sourceImgRef = useRef(null);
    const processingRef = useRef(false);
    const pendingRef = useRef(false);
    const grainSeedRef = useRef(Date.now());
    const workerRef = useRef(null);
    const generationRef = useRef(0);

    const [intensity, setIntensity] = useState(0.3);
    const [grainSize, setGrainSize] = useState(1.0);
    const [colorGrain, setColorGrain] = useState(false);
    const [roughness, setRoughness] = useState(0.5);
    const [blendMode, setBlendMode] = useState('overlay');
    const [shadowsOnly, setShadowsOnly] = useState(false);
    const [temperature, setTemperature] = useState(0);
    const [vignette, setVignette] = useState(0);

    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!visible || !sourceImageUrl) return;
        setLoading(true);
        grainSeedRef.current = Date.now(); // New seed each time panel opens
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => { sourceImgRef.current = img; setLoading(false); };
        img.onerror = () => setLoading(false);
        img.src = proxyUrl || sourceImageUrl;
    }, [visible, sourceImageUrl, proxyUrl]);

    // Effects worker lifecycle
    useEffect(() => {
        try {
            workerRef.current = new Worker(
                new URL('../workers/effectsWorker.js', import.meta.url)
            );
        } catch (err) {
            console.warn('Failed to create effects worker, falling back to main thread:', err);
        }
        return () => {
            if (workerRef.current) {
                workerRef.current.terminate();
                workerRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        if (!visible || !sourceImgRef.current) return;
        renderGrain();
    }, [visible, intensity, grainSize, colorGrain, roughness, blendMode, shadowsOnly, temperature, vignette, loading]);

    const renderGrain = useCallback(() => {
        if (!sourceImgRef.current || !previewCanvasRef.current) return;
        if (processingRef.current) { pendingRef.current = true; return; }
        processingRef.current = true;

        const worker = workerRef.current;
        const img = sourceImgRef.current;
        const maxDim = 800;
        const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);

        if (worker) {
            const generation = ++generationRef.current;
            const tmpCanvas = document.createElement('canvas');
            tmpCanvas.width = w; tmpCanvas.height = h;
            const tmpCtx = tmpCanvas.getContext('2d');
            tmpCtx.drawImage(img, 0, 0, w, h);
            const srcBuf = tmpCtx.getImageData(0, 0, w, h).data.buffer.slice(0);
            const alphaBuf = tmpCtx.getImageData(0, 0, w, h).data.buffer.slice(0);

            worker.onmessage = (ev) => {
                if (ev.data.generation !== generationRef.current) return;
                const result = new Uint8ClampedArray(ev.data.resultPixels);
                const preview = previewCanvasRef.current;
                if (preview) {
                    preview.width = ev.data.width; preview.height = ev.data.height;
                    preview.getContext('2d').putImageData(new ImageData(result, ev.data.width, ev.data.height), 0, 0);
                }
                processingRef.current = false;
                if (pendingRef.current) { pendingRef.current = false; renderGrain(); }
            };

            worker.postMessage({
                type: 'grain', pixels: srcBuf, alphaPixels: alphaBuf,
                width: w, height: h, temperature, grainSize, grainIntensity: intensity,
                roughness, colorGrain, shadowsOnly, blendMode, vignette,
                seed: grainSeedRef.current, generation,
            }, [srcBuf, alphaBuf]);
        } else {
            requestAnimationFrame(() => {
                try { doRender(previewCanvasRef.current, 800); }
                finally {
                    processingRef.current = false;
                    if (pendingRef.current) { pendingRef.current = false; renderGrain(); }
                }
            });
        }
    }, [intensity, grainSize, colorGrain, roughness, blendMode, shadowsOnly, temperature, vignette]);

    // Seeded pseudo-random for consistent grain
    const seededRandom = (seed) => {
        let s = seed;
        return () => {
            s = (s * 1103515245 + 12345) & 0x7fffffff;
            return s / 0x7fffffff;
        };
    };

    const doRender = (canvas, maxDim) => {
        const img = sourceImgRef.current;
        if (!img || !canvas) return;

        const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);

        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, w, h);

        // Temperature shift
        if (temperature !== 0) {
            const tempData = ctx.getImageData(0, 0, w, h);
            const td = tempData.data;
            const warmth = temperature * 30;
            for (let i = 0; i < td.length; i += 4) {
                td[i] = Math.max(0, Math.min(255, td[i] + warmth));       // R
                td[i + 2] = Math.max(0, Math.min(255, td[i + 2] - warmth)); // B
            }
            ctx.putImageData(tempData, 0, 0);
        }

        // Generate grain texture
        const gs = Math.max(1, Math.round(grainSize));
        const grainW = Math.ceil(w / gs);
        const grainH = Math.ceil(h / gs);

        const grainCanvas = document.createElement('canvas');
        grainCanvas.width = grainW;
        grainCanvas.height = grainH;
        const grainCtx = grainCanvas.getContext('2d');
        const grainData = grainCtx.getImageData(0, 0, grainW, grainH);
        const gd = grainData.data;

        const rng = seededRandom(grainSeedRef.current);

        for (let i = 0; i < gd.length; i += 4) {
            const px = (i / 4) % grainW;
            const py = Math.floor(i / 4 / grainW);

            // Base noise value
            let n = rng();

            // Roughness: push towards extremes
            if (roughness > 0) {
                n = Math.pow(n, 1 + roughness);
                if (rng() > 0.5) n = 1 - n;
            }

            // Shadows-only: reduce grain in highlights
            if (shadowsOnly) {
                const srcX = Math.min(w - 1, Math.round(px * gs));
                const srcY = Math.min(h - 1, Math.round(py * gs));
                const srcIdx = (srcY * w + srcX) * 4;
                const imgData = ctx.getImageData(srcX, srcY, 1, 1).data;
                const lum = (imgData[0] * 0.299 + imgData[1] * 0.587 + imgData[2] * 0.114) / 255;
                n = n * (1 - lum);
            }

            const v = Math.round(n * 255);

            if (colorGrain) {
                gd[i] = Math.round(rng() * 255);
                gd[i + 1] = Math.round(rng() * 255);
                gd[i + 2] = Math.round(rng() * 255);
            } else {
                gd[i] = v;
                gd[i + 1] = v;
                gd[i + 2] = v;
            }
            gd[i + 3] = 255;
        }

        grainCtx.putImageData(grainData, 0, 0);

        // Composite grain over image
        ctx.save();
        ctx.globalAlpha = intensity;
        ctx.globalCompositeOperation = blendMode === 'overlay' ? 'overlay'
            : blendMode === 'softlight' ? 'soft-light'
                : blendMode === 'screen' ? 'screen'
                    : 'multiply';

        // Scale grain up if grainSize > 1
        ctx.imageSmoothingEnabled = false;
        ctx.drawImage(grainCanvas, 0, 0, w, h);
        ctx.restore();

        // Vignette
        if (vignette > 0) {
            const gradient = ctx.createRadialGradient(w / 2, h / 2, Math.min(w, h) * 0.3, w / 2, h / 2, Math.max(w, h) * 0.7);
            gradient.addColorStop(0, 'rgba(0,0,0,0)');
            gradient.addColorStop(1, `rgba(0,0,0,${vignette})`);
            ctx.fillStyle = gradient;
            ctx.fillRect(0, 0, w, h);
        }

        // Alpha mask — preserve original transparency
        const alphaCanvas = document.createElement('canvas');
        alphaCanvas.width = w;
        alphaCanvas.height = h;
        const alphaCtx = alphaCanvas.getContext('2d');
        alphaCtx.drawImage(img, 0, 0, w, h);
        ctx.globalCompositeOperation = 'destination-in';
        ctx.drawImage(alphaCanvas, 0, 0);
        ctx.globalCompositeOperation = 'source-over';
    };

    const handleApply = useCallback(() => {
        const img = sourceImgRef.current;
        if (!img) return;
        const worker = workerRef.current;
        const w = img.width, h = img.height;

        if (worker) {
            const generation = ++generationRef.current;
            const tmpCanvas = document.createElement('canvas');
            tmpCanvas.width = w; tmpCanvas.height = h;
            const tmpCtx = tmpCanvas.getContext('2d');
            tmpCtx.drawImage(img, 0, 0, w, h);
            const srcBuf = tmpCtx.getImageData(0, 0, w, h).data.buffer.slice(0);
            const alphaBuf = tmpCtx.getImageData(0, 0, w, h).data.buffer.slice(0);

            worker.onmessage = (ev) => {
                if (ev.data.generation !== generationRef.current) return;
                const result = new Uint8ClampedArray(ev.data.resultPixels);
                const outCanvas = document.createElement('canvas');
                outCanvas.width = ev.data.width; outCanvas.height = ev.data.height;
                outCanvas.getContext('2d').putImageData(new ImageData(result, ev.data.width, ev.data.height), 0, 0);
                onApply?.(outCanvas.toDataURL('image/png'));
            };

            worker.postMessage({
                type: 'grain', pixels: srcBuf, alphaPixels: alphaBuf,
                width: w, height: h, temperature, grainSize, grainIntensity: intensity,
                roughness, colorGrain, shadowsOnly, blendMode, vignette,
                seed: grainSeedRef.current, generation,
            }, [srcBuf, alphaBuf]);
        } else {
            const fullCanvas = document.createElement('canvas');
            doRender(fullCanvas, Math.max(img.width, img.height));
            onApply?.(fullCanvas.toDataURL('image/png'));
        }
    }, [intensity, grainSize, colorGrain, roughness, blendMode, shadowsOnly, temperature, vignette, onApply]);

    const sliderRow = (label, min, max, value, onChange, step = 1) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <Text style={{ color: '#8b949e', fontSize: 11, minWidth: 80, whiteSpace: 'nowrap' }}>{label}</Text>
            <Slider min={min} max={max} step={step} value={value} onChange={onChange}
                style={{ flex: 1, margin: 0 }}
                styles={{ track: { background: '#fbbf24' }, rail: { background: '#30363d' }, handle: { borderColor: '#fbbf24', width: 12, height: 12 } }}
            />
            <Text style={{ color: '#fbbf24', fontSize: 11, fontFamily: 'monospace', minWidth: 36, textAlign: 'right' }}>
                {step < 1 ? value.toFixed(2) : value}
            </Text>
        </div>
    );

    const toggleRow = (label, value, onChange) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <Text style={{ color: '#8b949e', fontSize: 11, minWidth: 80 }}>{label}</Text>
            <Switch checked={value} onChange={onChange} size="small" style={{ background: value ? '#fbbf24' : '#30363d' }} />
        </div>
    );

    if (!visible) return null;

    return (
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 110, display: 'flex', background: 'rgba(13, 17, 23, 0.95)', pointerEvents: 'auto' }}
            onClick={(e) => e.stopPropagation()}>
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, overflow: 'hidden' }}>
                {loading ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
                        <Spin size="large" /><Text style={{ color: '#8b949e' }}>Loading image...</Text>
                    </div>
                ) : (
                    <canvas ref={previewCanvasRef} style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: 8, boxShadow: '0 8px 40px rgba(0,0,0,0.6)', border: '1px solid #30363d' }} />
                )}
            </div>

            <div style={{ width: 310, flexShrink: 0, borderLeft: '1px solid #30363d', display: 'flex', flexDirection: 'column', background: '#0d1117' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderBottom: '1px solid #30363d', background: '#161b22', flexShrink: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 16 }}>⣿</span>
                        <span style={{ color: '#c9d1d9', fontWeight: 600, fontSize: 14 }}>Grain</span>
                    </div>
                    <Button type="text" size="small" onClick={onClose} style={{ color: '#8b949e', fontSize: 12, padding: '2px 6px' }}>✕</Button>
                </div>

                <div style={{ flex: 1, overflowY: 'auto', padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <div>
                        <Text style={{ color: '#fbbf24', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>Grain</Text>
                        <div style={{ marginTop: 8 }}>
                            {sliderRow('Intensity', 0, 1, intensity, (v) => setIntensity(v), 0.01)}
                            {sliderRow('Size', 0.5, 4.0, grainSize, (v) => setGrainSize(v), 0.1)}
                            {sliderRow('Roughness', 0, 1, roughness, (v) => setRoughness(v), 0.01)}
                            {toggleRow('Color Grain', colorGrain, setColorGrain)}
                            {toggleRow('Shadows Only', shadowsOnly, setShadowsOnly)}
                        </div>
                    </div>

                    <div>
                        <Text style={{ color: '#fbbf24', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>Blend Mode</Text>
                        <Select value={blendMode} onChange={setBlendMode} style={{ width: '100%', marginTop: 6 }}
                            options={[
                                { value: 'overlay', label: 'Overlay' },
                                { value: 'softlight', label: 'Soft Light' },
                                { value: 'screen', label: 'Screen' },
                                { value: 'multiply', label: 'Multiply' },
                            ]}
                        />
                    </div>

                    <div>
                        <Text style={{ color: '#fbbf24', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>Film</Text>
                        <div style={{ marginTop: 8 }}>
                            {sliderRow('Temperature', -0.5, 0.5, temperature, (v) => setTemperature(v), 0.01)}
                            {sliderRow('Vignette', 0, 1, vignette, (v) => setVignette(v), 0.01)}
                        </div>
                    </div>

                    <Button size="small" type="text"
                        onClick={() => { grainSeedRef.current = Date.now(); renderGrain(); }}
                        style={{ color: '#6e7681', fontSize: 11, alignSelf: 'flex-start' }}>
                        🎲 Re-roll Grain
                    </Button>
                </div>

                <div style={{ padding: '12px 14px', borderTop: '1px solid #30363d', display: 'flex', gap: 8, flexShrink: 0, background: '#161b22' }}>
                    <Button onClick={onClose} style={{ flex: 1, background: '#21262d', borderColor: '#30363d', color: '#c9d1d9' }}>Cancel</Button>
                    <Button type="primary" onClick={handleApply} style={{ flex: 1, background: '#fbbf24', borderColor: '#fbbf24', color: '#000' }}>Apply</Button>
                </div>
            </div>
        </div>
    );
};

export default GrainEditorPanel;
